import React, { useState, useEffect, useCallback } from "react";
import { Table, Modal } from "react-bootstrap";
import { ChevronRight, Mail, Inbox } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import ResultsPagination from "../components/ResultsPagination";
import Dropdown from "react-bootstrap/Dropdown";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/TenderList.css";

const TenderList = () => {
  const { role, user } = useAuth();
  const navigate = useNavigate();
  const safeRole = role?.toLowerCase() || "";
  const isCustomer = safeRole.includes("customer");
  const isOperations = safeRole.includes("operations");
  const isAdmin = safeRole.includes("admin");
  const isSales = safeRole.includes("sales");
  const isStaff = isOperations || isAdmin || isSales;

  const [activeTab, setActiveTab] = useState("current");
  const [tenderData, setTenderData] = useState([]);
  const [activePage, setActivePage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [uniqueCustomers, setUniqueCustomers] = useState([]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tenderToDelete, setTenderToDelete] = useState(null);

  const [filters, setFilters] = useState({
    timeRange: "",
    startDate: "",
    endDate: "",
    status: "All",
    customer: ""
  });

  const fetchTenders = useCallback(async () => {
    try {
      if (!user?.id) return;
      const token = localStorage.getItem("token");

      let timeRangeParam = "";
      if (filters.timeRange === "1 wk") {
        timeRangeParam = "7days";
      } else if (filters.timeRange === "1 mo" || filters.timeRange === "3 mo") {
        timeRangeParam = "30days";
      }

      let startDateParam = filters.startDate || "";
      let endDateParam = filters.endDate || "";

      if (startDateParam) {
        try {
          const dateObj = new Date(startDateParam);
          if (!isNaN(dateObj.getTime())) {
            startDateParam = dateObj.toISOString().split("T")[0];
          }
        } catch (e) {}
      }

      if (endDateParam) {
        try {
          const dateObj = new Date(endDateParam);
          if (!isNaN(dateObj.getTime())) {
            endDateParam = dateObj.toISOString().split("T")[0];
          }
        } catch (e) {}
      }

      const hasFilters =
        filters.status !== "All" ||
        filters.customer ||
        startDateParam ||
        endDateParam ||
        filters.timeRange;

      const params = {
        userId: isStaff ? null : user?.id,
        role: safeRole,
        page: activePage,
        limit: perPage,
        status: filters.status,
        timeRange: timeRangeParam,
        customerFilter: filters.customer,
        startDate: startDateParam,
        endDate: endDateParam,
        activeTab: (isAdmin || isOperations) && !hasFilters ? activeTab : ""
      };

      const res = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/api/tender/tender-list`,
        {
          params,
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (res.data.success) {
        setTenderData(res.data.applications || []);
        setTotalPages(res.data.totalPages || 1);
        setTotalRecords(res.data.total || 0);

        if (res.data.globalCustomers && isStaff) {
          setUniqueCustomers(res.data.globalCustomers);
        } else if (isStaff) {
          const customersFromApps = [
            ...new Set(
              (res.data.applications || [])
                .filter(
                  (app) =>
                    app.customer && app.customer !== "Unknown Customer"
                )
                .map((app) => app.customer)
            )
          ].map((name) => ({ id: name, name }));
          setUniqueCustomers(customersFromApps);
        }
      }
    } catch (err) {
      console.error("Fetch tenders failed:", err);
    }
  }, [
    user?.id,
    isStaff,
    activePage,
    perPage,
    filters,
    activeTab,
    isAdmin,
    isOperations,
    safeRole
  ]);

  useEffect(() => {
    fetchTenders();
  }, [fetchTenders]);

  useEffect(() => {
    setActivePage(1);
  }, [
    filters.status,
    filters.customer,
    filters.startDate,
    filters.endDate,
    filters.timeRange
  ]);

  // ✅ Prefetch tender details on hover/touch
  const prefetchTender = useCallback(
    async (tenderId) => {
      if (!tenderId) return;
      const key = `tender_prefetch_${tenderId}`;
      if (sessionStorage.getItem(key)) return;

      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `${process.env.REACT_APP_BASE_API_URL}/api/tenders/${tenderId}`,
          {
            params: { userId: user?.id },
            headers: { Authorization: token ? `Bearer ${token}` : "" }
          }
        );

        if (res.data?.tender) {
          try {
            sessionStorage.setItem(key, JSON.stringify(res.data));
          } catch (e) {
            // storage full or disabled — silently ignore
          }
        }
      } catch (e) {
        // silent prefetch failure
      }
    },
    [user?.id]
  );

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setActivePage(1);
  };

  const resetFilters = () => {
    setFilters({
      timeRange: "",
      startDate: "",
      endDate: "",
      status: "All",
      customer: ""
    });
    setActivePage(1);
  };

  const handleSubmit = () => {
    if (isCustomer) {
      console.log("User submitting tender...");
    } else if (isSales || isOperations) {
      console.log("Sending mail...");
    }
  };

  const handleEdit = (tender) => {
    navigate(`/tender/${tender.tenderId}`, {
      state: { task: tender, sourceTab: "tenderList", edit: true }
    });
  };

  const confirmDelete = (id) => {
    setTenderToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!tenderToDelete) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${process.env.REACT_APP_BASE_API_URL}/api/tender-applications/${tenderToDelete}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      fetchTenders();
      setShowDeleteModal(false);
      setTenderToDelete(null);
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete the application.");
    }
  };

  const handlePerPageChange = (val) => {
    setPerPage(Number(val));
    setActivePage(1);
  };

  const formatDate = (date) => {
    if (!date || date === "—" || date === "") return "—";
    if (
      typeof date === "string" &&
      (date.includes(",") || date.split(".").length - 1 >= 2)
    )
      return "—";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  };

  const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
    <div
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
    >
      {children}
    </div>
  ));

  const MobileTenderCard = ({ tender, idx }) => {
    const key = tender.id || `tender-${idx}`;

    return (
      <div key={key} className="mobile-tender-card">
        <div className="mobile-tender-header">
          <div className="mobile-tender-title">{tender.title || "—"}</div>
          <div className="d-flex align-items-center gap-2 flex-shrink-0">
            <Link
              to={`/tender/${tender.tenderId}`}
              state={{ task: tender, sourceTab: "tenderList" }}
              onTouchStart={() => prefetchTender(tender.tenderId)}
              onMouseEnter={() => prefetchTender(tender.tenderId)}
            >
              <img
                src="/images/eye-icon.svg"
                alt="view"
                style={{
                  width: "20px",
                  height: "20px",
                  filter:
                    "invert(35%) sepia(20%) saturate(600%) hue-rotate(150deg)"
                }}
              />
            </Link>
            <Dropdown align="end">
              <Dropdown.Toggle as={CustomToggle} className="three-dot-toggle">
                <span
                  style={{ fontSize: "20px", color: "#64748b", lineHeight: 1 }}
                >
                  ⋮
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu
                renderOnMount
                popperConfig={{ strategy: "fixed" }}
              >
                {activeTab !== "previous" && (
                  <Dropdown.Item onClick={() => handleEdit(tender)}>
                    Edit
                  </Dropdown.Item>
                )}
                <Dropdown.Item
                  onClick={() => confirmDelete(tender.id)}
                  className="text-danger"
                >
                  Delete
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        <div className="mobile-tender-grid">
          <div className="mobile-tender-cell">
            <div className="mobile-tender-label">Department</div>
            <div className="mobile-tender-value">{tender.org || "—"}</div>
          </div>
          <div className="mobile-tender-cell">
            <div className="mobile-tender-label">Ministry</div>
            <div className="mobile-tender-value">{tender.ministry || "—"}</div>
          </div>
          <div className="mobile-tender-cell">
            <div className="mobile-tender-label">State</div>
            <div className="mobile-tender-value">{tender.state || "—"}</div>
          </div>
          <div className="mobile-tender-cell">
            <div className="mobile-tender-label">
              {isSales ? "Tender Value" : "Deadline"}
            </div>
            <div
              className={`mobile-tender-value ${
                !isSales ? "mobile-tender-value--date" : ""
              }`}
            >
              {isSales
                ? tender.tender_value_display || "—"
                : formatDate(tender.deadline)}
            </div>
          </div>
          <div className="mobile-tender-cell">
            <div className="mobile-tender-label">EMD</div>
            <div className="mobile-tender-value">
              {tender.emd_value_display || "—"}
            </div>
          </div>
          <div className="mobile-tender-cell">
            <div className="mobile-tender-label">
              {isSales ? "Submission Date" : "Tender Value"}
            </div>
            <div
              className={`mobile-tender-value ${
                isSales ? "mobile-tender-value--date" : ""
              }`}
            >
              {isSales
                ? formatDate(tender.deadline)
                : tender.tender_value_display || "—"}
            </div>
          </div>
        </div>

        {isCustomer && tender.id && (
          <div className="mobile-tender-footer-row">
            <div className="mobile-tender-cell">
              <div className="mobile-tender-label">Level</div>
              <div className="mobile-tender-value">
                {tender.level === "level2"
                  ? "Level 2"
                  : tender.level === "level3"
                  ? "Level 3"
                  : tender.level === "completed"
                  ? "Completed"
                  : "Level 1"}
              </div>
            </div>
            <div className="mobile-tender-cell">
              <div className="mobile-tender-label">Status</div>
              <span
                className={`status-pill ${
                  tender.status === "approved"
                    ? "status-approved"
                    : tender.status === "rejected"
                    ? "status-rejected"
                    : tender.status === "shortlisted"
                    ? "status-shortlisted"
                    : "status-pending"
                }`}
              >
                {tender.status === "approved"
                  ? "Approved"
                  : tender.status === "rejected"
                  ? "Rejected"
                  : tender.status === "shortlisted"
                  ? "Shortlisted"
                  : "Pending"}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <nav
        aria-label="breadcrumb"
        className="d-none d-lg-block"
        style={{ "--bs-breadcrumb-divider": "''" }}
      >
        <ol
          className="breadcrumb d-flex align-items-center mb-0"
          style={{ fontSize: "14px" }}
        >
          <li className="breadcrumb-item d-flex align-items-center">
            <Link to="/" className="text-decoration-none text-secondary">
              Home
            </Link>
            <ChevronRight size={16} className="mx-1 text-secondary" />
          </li>
          <li
            className="breadcrumb-item fw-semibold"
            style={{ color: "#2F7A87" }}
          >
            Tender List
          </li>
        </ol>
      </nav>

      {(isCustomer || isOperations || isAdmin) && (
        <div className="d-block d-lg-none customer-mobile-wrapper">
          <div className="customer-mobile-breadcrumb">
            <Link
              to="/"
              className="text-decoration-none"
              style={{ color: "#6b7280", fontSize: "12px" }}
            >
              Home
            </Link>
            <ChevronRight size={12} color="#9ca3af" />
            <span
              style={{ color: "#2F7A87", fontSize: "12px", fontWeight: "600" }}
            >
              Task List
            </span>
          </div>

          <div className="d-flex align-items-center gap-2 mb-1">
            <h4 className="tender-list-title mb-0">Tender list</h4>
          </div>
          <p className="tender-list-subtitle mb-3">
            View assigned queries, track leads, and manage sales activities in
            one place.
          </p>

          {(isOperations || isAdmin) && (
            <div className="d-flex gap-4 mb-3">
              {["current", "previous"].map((tab) => (
                <span
                  key={tab}
                  onClick={() => {
                    setActiveTab(tab);
                    setActivePage(1);
                  }}
                  style={{
                    cursor: "pointer",
                    paddingBottom: "6px",
                    borderBottom:
                      activeTab === tab
                        ? "2px solid #2F7A87"
                        : "2px solid transparent",
                    color: activeTab === tab ? "#2F7A87" : "#6c757d",
                    fontWeight: activeTab === tab ? "600" : "400",
                    fontSize: "15px"
                  }}
                >
                  {tab === "current" ? "Current" : "Previous"}
                </span>
              ))}
            </div>
          )}

          <div className="customer-mobile-filter-box">
            <div>
              <div className="cmf-label">Time Range:</div>
              <div className="cmf-btn-group">
                {["1 wk", "1 mo", "3 mo"].map((r) => (
                  <button
                    key={r}
                    className={`cmf-range-btn ${
                      filters.timeRange === r ? "cmf-range-btn--active" : ""
                    }`}
                    onClick={() => handleFilterChange("timeRange", r)}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            <div className="cmf-date-row">
              <div className="cmf-date-col">
                <div className="cmf-label">Start</div>
                <input
                  type="date"
                  className="cmf-input"
                  value={filters.startDate}
                  onChange={(e) =>
                    handleFilterChange("startDate", e.target.value)
                  }
                />
              </div>
              <div className="cmf-date-col">
                <div className="cmf-label">End</div>
                <input
                  type="date"
                  className="cmf-input"
                  value={filters.endDate}
                  onChange={(e) =>
                    handleFilterChange("endDate", e.target.value)
                  }
                />
              </div>
            </div>

            <div className="cmf-status-row">
              <div style={{ flex: 1 }}>
                <div className="cmf-label">Status</div>
                <select
                  className="cmf-input"
                  value={filters.status}
                  onChange={(e) =>
                    handleFilterChange("status", e.target.value)
                  }
                >
                  <option value="All">All</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="shortlisted">Shortlisted</option>
                </select>
              </div>
              <button className="cmf-reset-btn" onClick={resetFilters}>
                <img
                  src="/images/filter.svg"
                  alt="reset"
                  style={{ width: "16px", height: "16px" }}
                />
              </button>
            </div>

            {isStaff && (
              <div className="mt-3">
                <div className="cmf-label">Customer</div>
                <select
                  className="cmf-input"
                  value={filters.customer}
                  onChange={(e) =>
                    handleFilterChange("customer", e.target.value)
                  }
                >
                  <option value="">All Customers</option>
                  {uniqueCustomers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="customer-mobile-pagination-top">
            <ResultsPagination
              activePage={activePage}
              setActivePage={setActivePage}
              perPage={perPage}
              setPerPage={handlePerPageChange}
              totalPages={totalPages}
            />
          </div>

          <div className="mobile-cards-wrapper">
            {tenderData.length === 0 ? (
              <div className="d-flex flex-column align-items-center justify-content-center text-muted py-5 text-center bg-white rounded-3 shadow-sm border mt-3">
                <Inbox
                  size={48}
                  className="mb-3 text-secondary"
                  style={{ opacity: 0.5 }}
                />
                <h6
                  className="fw-semibold mb-1"
                  style={{ color: "#4b5563" }}
                >
                  No tenders found
                </h6>
                <p className="small mb-0" style={{ color: "#9ca3af" }}>
                  Adjust your filters or try a different search.
                </p>
              </div>
            ) : (
              tenderData.map((tender, idx) => (
                <MobileTenderCard
                  key={tender.id || idx}
                  tender={tender}
                  idx={idx}
                />
              ))
            )}
          </div>

          <div className="d-flex align-items-center justify-content-between px-1 py-2 mb-16">
            <div className="d-flex align-items-center gap-2">
              <span style={{ fontSize: "13px", color: "#6b7280" }}>Show</span>
              <select
                className="cmf-show-select"
                value={perPage}
                onChange={(e) => handlePerPageChange(e.target.value)}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
              </select>
            </div>
            <ResultsPagination
              activePage={activePage}
              setActivePage={setActivePage}
              perPage={perPage}
              setPerPage={handlePerPageChange}
              totalPages={totalPages}
            />
          </div>
        </div>
      )}

      {isSales && (
        <div className="d-block d-lg-none sales-mobile-wrapper">
          <div className="sales-mobile-header">
            <div className="d-flex align-items-center gap-2">
              <span
                style={{
                  fontSize: "20px",
                  cursor: "pointer",
                  color: "#374151"
                }}
                onClick={() => navigate(-1)}
              >
                ←
              </span>
              <div>
                <h4 className="tender-list-title mb-0">Tender list</h4>
                <p className="tender-list-subtitle mb-0">
                  List out the tenders to send to customers
                </p>
              </div>
            </div>
            <button
              className="sales-send-email-btn"
              onClick={handleSubmit}
            >
              <Mail size={15} />
              Send email
            </button>
          </div>

          <div className="mobile-cards-wrapper">
            {tenderData.map((tender, idx) => (
              <MobileTenderCard
                key={tender.id || idx}
                tender={tender}
                idx={idx}
              />
            ))}
          </div>

          <div className="d-flex align-items-center justify-content-between px-1 py-2 mb-16">
            <span className="text-muted small">
              Showing{" "}
              {totalRecords === 0 ? 0 : (activePage - 1) * perPage + 1}–
              {Math.min(activePage * perPage, totalRecords)} of {totalRecords}
            </span>
            <ResultsPagination
              activePage={activePage}
              setActivePage={setActivePage}
              perPage={perPage}
              setPerPage={handlePerPageChange}
              totalPages={totalPages}
            />
          </div>
        </div>
      )}

      <div className="d-none d-lg-block ms-4">
        <div className="mt-4 d-flex align-items-flex-start">
          <div>
            <h4 className="tender-list-title mb-1">Tender list</h4>
            <p className="tender-list-subtitle mb-0">
              {isSales
                ? "List out the tenders to send to customers"
                : "View assigned queries, track leads, and manage sales activities in one place."}
            </p>
          </div>
        </div>

        {(isAdmin || isOperations) && (
          <div className="d-flex gap-4 mt-3">
            {["current", "previous"].map((tab) => (
              <span
                key={tab}
                onClick={() => {
                  setActiveTab(tab);
                  setActivePage(1);
                }}
                style={{
                  cursor: "pointer",
                  paddingBottom: "6px",
                  borderBottom:
                    activeTab === tab
                      ? "2px solid #2F7A87"
                      : "2px solid transparent",
                  color: activeTab === tab ? "#2F7A87" : "#6c757d",
                  fontWeight: activeTab === tab ? "600" : "400"
                }}
              >
                {tab === "current" ? "Current" : "Previous"}
              </span>
            ))}
          </div>
        )}

        <div className={isSales ? "mt-4" : "rounded border mt-4"}>
          {(isCustomer || isAdmin || isOperations) && (
            <div className="rounded px-0">
              <div className="d-flex align-items-center flex-wrap bg-white rounded-1">
                <div
                  className="px-3 bg-white rounded d-flex align-items-center"
                  style={{ paddingTop: "30px", paddingBottom: "2px" }}
                >
                  <span>Time Range:</span>
                  <div className="btn-group ms-3">
                    {["1 wk", "1 mo", "3 mo"].map((range) => (
                      <button
                        key={range}
                        className="btn border date-sizes"
                        onClick={() => handleFilterChange("timeRange", range)}
                        style={{
                          borderRadius: "30px",
                          marginRight: "8px",
                          backgroundColor:
                            filters.timeRange === range
                              ? "#2F7A87"
                              : "transparent",
                          color:
                            filters.timeRange === range ? "#fff" : "#6c757d",
                          borderColor:
                            filters.timeRange === range
                              ? "#2F7A87"
                              : "#6c757d"
                        }}
                      >
                        {range}
                      </button>
                    ))}
                  </div>
                </div>
                <div
                  className="px-3 bg-white rounded d-flex gap-2 align-items-center"
                  style={{ paddingTop: "30px", paddingBottom: "2px" }}
                >
                  <span>Start:</span>
                  <input
                    type="date"
                    className="form-control date-sizes mb-0"
                    value={filters.startDate}
                    onChange={(e) =>
                      handleFilterChange("startDate", e.target.value)
                    }
                    style={{
                      width: "150px",
                      display: "block",
                      borderRadius: "30px"
                    }}
                  />
                </div>
                <div
                  className="px-3 bg-white rounded d-flex gap-2 align-items-center"
                  style={{ paddingTop: "30px", paddingBottom: "2px" }}
                >
                  <span>End:</span>
                  <input
                    type="date"
                    className="form-control date-sizes mb-0"
                    value={filters.endDate}
                    onChange={(e) =>
                      handleFilterChange("endDate", e.target.value)
                    }
                    style={{
                      width: "150px",
                      display: "block",
                      borderRadius: "30px"
                    }}
                  />
                </div>
                {isStaff && (
                  <div
                    className="ms-3 px-3 bg-white rounded d-flex gap-2 align-items-center"
                    style={{ paddingTop: "30px", paddingBottom: "2px" }}
                  >
                    <span>Customer:</span>
                    <select
                      className="form-select"
                      style={{ borderRadius: "30px", width: "160px" }}
                      value={filters.customer}
                      onChange={(e) =>
                        handleFilterChange("customer", e.target.value)
                      }
                    >
                      <option value="">All</option>
                      {uniqueCustomers.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div
                  className="ms-3 px-3 bg-white rounded d-flex gap-1 align-items-center"
                  style={{ paddingTop: "30px", paddingBottom: "2px" }}
                >
                  <span>Status:</span>
                  <select
                    className="form-select"
                    style={{ borderRadius: "30px", width: "140px" }}
                    value={filters.status}
                    onChange={(e) =>
                      handleFilterChange("status", e.target.value)
                    }
                  >
                    <option value="All">All</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="shortlisted">Shortlisted</option>
                  </select>
                </div>
                <div
                  className="d-flex align-items-center gap-2 px-3"
                  style={{
                    cursor: "pointer",
                    paddingTop: "30px",
                    paddingBottom: "2px",
                    color: "#333"
                  }}
                  onClick={resetFilters}
                  title="Reset filters"
                >
                  <img
                    src="/images/filter.svg"
                    alt="Reset"
                    style={{ width: "16px", height: "16px" }}
                  />
                  <span style={{ fontWeight: 400, fontSize: "14px" }}>
                    Reset Filters
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="table-container mt-4 desktop-table-wrapper rounded-0">
            <div
              className="table-responsive rounded-0"
              style={{ width: "100%", overflowX: "auto" }}
            >
              <Table
                hover
                className="content-tender align-middle small mb-0 rounded-0"
                style={{
                  width: "100%",
                  minWidth: isCustomer ? "80rem" : "70rem",
                  tableLayout: "fixed",
                  borderRadius: 0
                }}
              >
                <thead
                  className="rounded-0"
                  style={{
                    backgroundColor: "#F9FAFB",
                    borderBottom: "1px solid #E5E7EB",
                    borderRadius: 0
                  }}
                >
                  <tr className="text-muted" style={{ fontWeight: 500 }}>
                    <th
                      style={{
                        width: "20%",
                        padding: "10px 8px",
                        border: "none",
                        borderRadius: 0
                      }}
                    >
                      Tender Name
                    </th>
                    <th
                      style={{ width: "15%", padding: "10px 8px", border: "none" }}
                    >
                      Department
                    </th>
                    <th
                      style={{ width: "12%", padding: "10px 8px", border: "none" }}
                    >
                      Ministry
                    </th>
                    <th
                      style={{ width: "10%", padding: "10px 8px", border: "none" }}
                    >
                      State
                    </th>
                    <th
                      style={{ width: "10%", padding: "10px 8px", border: "none" }}
                    >
                      Tender Value
                    </th>
                    <th
                      style={{ width: "10%", padding: "10px 8px", border: "none" }}
                    >
                      EMD Value
                    </th>
                    <th
                      style={{ width: "10%", padding: "10px 8px", border: "none" }}
                    >
                      Deadline
                    </th>
                    {isStaff && activeTab === "previous" && (
                      <th
                        style={{
                          width: "10%",
                          padding: "10px 8px",
                          border: "none"
                        }}
                      >
                        Status
                      </th>
                    )}
                    {isCustomer && (
                      <th
                        style={{ width: "8%", padding: "10px 8px", border: "none" }}
                      >
                        Level
                      </th>
                    )}
                    {isCustomer && (
                      <th
                        style={{
                          width: "10%",
                          padding: "10px 8px",
                          border: "none"
                        }}
                      >
                        Status
                      </th>
                    )}
                    <th
                      className="text-center"
                      style={{
                        width: "8%",
                        padding: "10px 8px",
                        border: "none",
                        borderRadius: 0
                      }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody style={{ borderTop: "none" }}>
                  {tenderData.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="text-center py-5">
                        <div className="d-flex flex-column align-items-center justify-content-center text-muted">
                          <Inbox
                            size={48}
                            className="mb-3 text-secondary"
                            style={{ opacity: 0.5 }}
                          />
                          <h5
                            className="fw-semibold mb-1"
                            style={{ color: "#4b5563" }}
                          >
                            No tenders found
                          </h5>
                          <p
                            className="small mb-0"
                            style={{ color: "#9ca3af" }}
                          >
                            Adjust your filters or try a different search to see
                            results.
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    tenderData.map((tender, idx) => (
                      <tr
                        key={tender.id || idx}
                        style={{
                          borderBottom: "1px solid rgba(229,231,235,1)"
                        }}
                      >
                        <td style={{ padding: "16px 12px", border: "none" }}>
                          <div
                            className="text-truncate fw-semibold text-dark"
                            title={tender.title}
                          >
                            {tender.title || "—"}
                          </div>
                        </td>
                        <td style={{ padding: "16px 12px", border: "none" }}>
                          <div className="text-truncate text-muted">
                            {tender.org || "—"}
                          </div>
                        </td>
                        <td style={{ padding: "16px 12px", border: "none" }}>
                          <div className="text-truncate">
                            {tender.ministry || "—"}
                          </div>
                        </td>
                        <td style={{ padding: "16px 12px", border: "none" }}>
                          <div className="text-truncate">
                            {tender.state || "—"}
                          </div>
                        </td>
                        <td style={{ padding: "16px 12px", border: "none" }}>
                          <div className="text-truncate text-dark fw-medium">
                            {tender.tender_value_display || "—"}
                          </div>
                        </td>
                        <td style={{ padding: "16px 12px", border: "none" }}>
                          <div className="text-truncate text-dark fw-medium">
                            {tender.emd_value_display || "—"}
                          </div>
                        </td>
                        <td style={{ padding: "16px 12px", border: "none" }}>
                          <div className="text-truncate text-muted">
                            {formatDate(tender.deadline)}
                          </div>
                        </td>
                        {isStaff && activeTab === "previous" && (
                          <td style={{ padding: "16px 12px", border: "none" }}>
                            <span
                              className={`status-pill ${
                                tender.status === "approved"
                                  ? "status-approved"
                                  : tender.status === "rejected"
                                  ? "status-rejected"
                                  : "status-pending"
                              }`}
                              style={{ fontSize: "13px" }}
                            >
                              {tender.status === "approved"
                                ? "Approved"
                                : tender.status === "rejected"
                                ? "Rejected"
                                : tender.status === "shortlisted"
                                ? "Shortlisted"
                                : "Pending"}
                            </span>
                          </td>
                        )}
                        {isCustomer && (
                          <>
                            <td
                              style={{ padding: "16px 12px", border: "none" }}
                            >
                              {tender.id ? (
                                <span
                                  className="status-pill level"
                                  style={{ fontSize: "13px" }}
                                >
                                  {tender.level === "level2"
                                    ? "Level 2"
                                    : tender.level === "level3"
                                    ? "Level 3"
                                    : tender.level === "completed"
                                    ? "Completed"
                                    : "Level 1"}
                                </span>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td
                              style={{ padding: "16px 12px", border: "none" }}
                            >
                              {tender.id ? (
                                <span
                                  className={`status-pill ${
                                    tender.status === "approved"
                                      ? "status-approved"
                                      : tender.status === "rejected"
                                      ? "status-rejected"
                                      : "status-pending"
                                  }`}
                                  style={{ fontSize: "13px" }}
                                >
                                  {tender.status === "approved"
                                    ? "Approved"
                                    : tender.status === "rejected"
                                    ? "Rejected"
                                    : tender.status === "shortlisted"
                                    ? "Shortlisted"
                                    : "Pending"}
                                </span>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                          </>
                        )}
                        <td
                          className="text-center"
                          style={{ padding: "16px 12px", border: "none" }}
                        >
                          <div className="d-flex justify-content-center align-items-center gap-3">
                            <Link
                              to={`/tender/${tender.tenderId}`}
                              state={{ task: tender, sourceTab: "tenderList" }}
                              onMouseEnter={() =>
                                prefetchTender(tender.tenderId)
                              }
                              onTouchStart={() =>
                                prefetchTender(tender.tenderId)
                              }
                            >
                              <img
                                src="/images/eye-icon.svg"
                                alt="view"
                                style={{ cursor: "pointer", width: "18px" }}
                              />
                            </Link>
                            <Dropdown align="end">
                              <Dropdown.Toggle
                                as={CustomToggle}
                                className="three-dot-toggle"
                              >
                                <span
                                  style={{
                                    fontSize: "18px",
                                    padding: "0 4px",
                                    color: "#64748b"
                                  }}
                                >
                                  ⋮
                                </span>
                              </Dropdown.Toggle>
                              <Dropdown.Menu
                                renderOnMount
                                popperConfig={{ strategy: "fixed" }}
                              >
                                {/* {activeTab !== "previous" && (
                                  <Dropdown.Item
                                    onClick={() => handleEdit(tender)}
                                  >
                                    Edit
                                  </Dropdown.Item>
                                )} */}
                                <Dropdown.Item
                                  onClick={() => confirmDelete(tender.id)}
                                  className="text-danger"
                                >
                                  Delete
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>

            <div className="table-footer gap-3 p-3 d-flex align-items-center justify-content-between">
              <span className="text-muted small">
                Showing{" "}
                {totalRecords === 0 ? 0 : (activePage - 1) * perPage + 1}–
                {Math.min(activePage * perPage, totalRecords)} of {totalRecords}
              </span>
              <ResultsPagination
                activePage={activePage}
                setActivePage={setActivePage}
                perPage={perPage}
                setPerPage={handlePerPageChange}
                totalPages={totalPages}
              />
            </div>
          </div>
        </div>

        {(isAdmin || isSales) && (
          <div
            style={{
              position: "fixed",
              bottom: "20px",
              right: "40px",
              zIndex: 1000
            }}
          >
            <button className="tender-submit-btn" onClick={handleSubmit}>
              <span className="d-flex align-items-center gap-2">
                <Mail size={16} />
                Send email
              </span>
            </button>
          </div>
        )}
      </div>

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header
          closeButton
          style={{ borderBottom: "none", paddingBottom: 0 }}
        >
          <Modal.Title className="fs-5 fw-semibold text-dark">
            Confirm Deletion
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-muted" style={{ fontSize: "15px" }}>
          Are you sure you want to delete this tender? This action cannot be
          undone.
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "none", paddingTop: 0 }}>
          <button
            className="btn btn-light"
            onClick={() => setShowDeleteModal(false)}
            style={{ fontWeight: 500 }}
          >
            Cancel
          </button>
          <button className="btn custom-del-btn" onClick={handleDelete}>
            Delete
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TenderList;